import useSWR, { SWRResponse } from "swr";

import { useGet } from "../api-client/useGet";
import { useNullableDealerAccountInfo } from "../stores/hooks/useNullableDealerAccountInfo";
import { useSecondaryManufacturersProp } from "../stores/hooks/useSecondaryManufacturersProp";
import {
  VehicleManufacturer,
  VehicleManufacturersPayload,
  getVehicleManufacturersEndpoint,
} from "./useGetVehicleManufacturers";

export const useBatchGetVehicleManufacturers = (
  vehicleYearIds: number[]
): SWRResponse<VehicleManufacturer[]> => {
  const dealer = useNullableDealerAccountInfo();
  const secondaryManufacturersProp = useSecondaryManufacturersProp();
  const { get: getVehicleManufacturers } = useGet();

  const response = useSWR<VehicleManufacturer[]>(
    vehicleYearIds.length ? vehicleYearIds.join() : null,
    async (): Promise<VehicleManufacturer[]> => {
      const promises = vehicleYearIds.map((vehicleYearId) =>
        getVehicleManufacturers<VehicleManufacturersPayload>(
          getVehicleManufacturersEndpoint,
          {
            dealerId: dealer?.dealerId || "",
            vehicleYearId: String(vehicleYearId),
          }
        )
      );
      const results = await Promise.all(promises);
      const data = results.map((result) => result.payload);
      const primary = data.flatMap(
        ({ primaryVehicleManufacturers }) => primaryVehicleManufacturers
      );
      const secondary = secondaryManufacturersProp
        ? data.flatMap(
            ({ secondaryVehicleManufacturers }) => secondaryVehicleManufacturers
          )
        : [];

      const map = new Map<number, VehicleManufacturer>();

      for (const manufacturer of [...primary, ...secondary]) {
        map.set(manufacturer.vehicleManufacturerId, manufacturer);
      }

      return [...map.values()];
    },
    {
      keepPreviousData: true,
    }
  );

  return response;
};
